import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Typography from "components/basic/typography";
import HomeChargerImage from "resources/images/HomeChargerImage.png";
import { useNavigate } from "react-router-dom";
import SignUpFormLayout from "../../components/SignUpFormLayout";
import AutoComplete from "components/basic/autoComplete";
import TextField from "components/basic/textField";
import { useSnack } from "hooks/useSnack";
import { useFieldErrors } from "hooks/useFieldErrors";
import { useMaskedInput } from "hooks/useMaskedInput";
import DatePicker from "components/basic/datePicker";
import {
  ELECTRICAL_LICENSE_NUMBER_PLACEHOLDER,
  formatElectricalLicenseNumber,
  todayIsoDate,
} from "pages/formTools";
import { provinceLabel, provinces } from "pages/calculator/data";
import UploadFile from "components/basic/uploadFile";
import { SignUpFormData, Vendor } from "../..";
import { validateSignature } from "./validation";

interface SignatureFormProps {
  signUpData: SignUpFormData;
}

const SignatureForm: React.FC<SignatureFormProps> = ({ signUpData }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const showSnack = useSnack();
  const { t } = useTranslation("auth");
  const { t: tAccount } = useTranslation("account");
  const { t: tCommon } = useTranslation();

  const { account, vendor, setVendor } = signUpData;

  const { errors, validateAll, revalidate } = useFieldErrors(validateSignature);
  const handleElectricalLicenseNumberChange = useMaskedInput(
    formatElectricalLicenseNumber,
  );

  // Applies an edit and re-checks any error already on screen.
  const updateField = (patch: Partial<Vendor>) => {
    const next = { ...vendor, ...patch };
    setVendor(next);
    revalidate(next);
  };

  const offeringEvInstallationTypes = [
    { id: 1, label: "Yes" },
    { id: 2, label: "No" },
  ];
  // Stored in English; the dropdown shows the translation.
  const picked = (id?: number) =>
    offeringEvInstallationTypes.find((option) => option.id === id)?.label ?? "";

  const offeringEvInstallationType = vendor.offeringEvInstallation
    ? offeringEvInstallationTypes.find(
        (opt) => opt.label === vendor.offeringEvInstallation,
      ) || null
    : null;
  const isOfferingEvInstallation = vendor.offeringEvInstallation === "Yes";

  const provinceType = vendor.province
    ? provinces.find((opt) => opt.name === vendor.province) || null
    : null;

  const handleNext = () => {
    if (!validateAll(vendor)) {
      showSnack(t("common.fix_errors"), "error");
      return;
    }

    if (!account.firstName.trim()) {
      showSnack(t("installation.timeout"), "error");
      navigate("?currentPage=emailForm");
    } else {
      //handleSignUp();
      navigate("?currentPage=vendorReviewForm");
    }
  };

  return (
    <SignUpFormLayout
      imageSrc={HomeChargerImage}
      backAction={{
        label: t("common.back"),
        color: "primary",
        onClick: () => navigate("?currentPage=businessRegistrationForm"),
      }}
      primaryAction={{
        label: t("installation.review_details"),
        color: "warning",
        onClick: handleNext,
      }}
    >
      <div>
        <Typography variant="body2" align="inherit">
          {t("installation.question")}
        </Typography>

        <AutoComplete
          name="offeringEvInstallation"
          value={offeringEvInstallationType?.id || null}
          options={offeringEvInstallationTypes.map(({ id, label }) => ({
            id,
            label: tAccount(label === "Yes" ? "yes" : "no"),
          }))}
          label={t("common.please_select")}
          onChange={(value) =>
            updateField({
              offeringEvInstallation: picked(value?.id),
              ...(picked(value?.id) !== "Yes"
                ? {
                    electricalLicense: null,
                    electricalLicenseURL: null,
                    electricalLicenseNumber: null,
                    electricalLicenseExpiryDate: "",
                  }
                : {}),
            })
          }
          size="tiny"
          textAlign="center"
          error={errors.offeringEvInstallation ?? undefined}
        />
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(1),
        }}
      >
        <Typography variant="body2" align="inherit">
          {t("installation.province")}
        </Typography>

        <AutoComplete
          name="province"
          value={provinceType?.id || null}
          options={provinces.map((prov) => ({
            id: prov.id,
            label: provinceLabel(tCommon, prov.code),
          }))}
          label={t("common.please_select")}
          onChange={(value) =>
            updateField({
              province:
                provinces.find(({ id }) => id === value?.id)?.name ?? "",
            })
          }
          size="tiny"
          textAlign="center"
          error={errors.province ?? undefined}
        />

        {isOfferingEvInstallation && (
          <>
            <UploadFile
              label={t("installation.upload_license")}
              onChange={(files) => {
                updateField({ electricalLicense: files });
              }}
              error={errors.electricalLicense ?? undefined}
              file={vendor.electricalLicense}
            />

            <div>
              <Typography variant="body2" align="inherit">
                {t("installation.license_number")}
              </Typography>

              <TextField
                placeHolder={ELECTRICAL_LICENSE_NUMBER_PLACEHOLDER}
                name="electricalLicenseNumber"
                value={vendor.electricalLicenseNumber ?? ""}
                size="tiny"
                textAlign="right"
                onChange={(event) =>
                  updateField({
                    electricalLicenseNumber:
                      handleElectricalLicenseNumberChange(
                        event,
                        vendor.electricalLicenseNumber ?? "",
                      ),
                  })
                }
                error={errors.electricalLicenseNumber ?? undefined}
              />
            </div>

            <div>
              <Typography variant="body2" align="inherit">
                {t("installation.license_expiry")}
              </Typography>

              <DatePicker
                value={vendor.electricalLicenseExpiryDate}
                minDate={todayIsoDate()}
                onChange={(electricalLicenseExpiryDate) =>
                  updateField({ electricalLicenseExpiryDate })
                }
                error={errors.electricalLicenseExpiryDate ?? undefined}
              />
            </div>
          </>
        )}
      </div>
    </SignUpFormLayout>
  );
};

export default SignatureForm;
